/**
 * Simulations List Page
 * Shows all user simulations with filtering options.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import simulationService from '../../services/simulationService';
import StatusIcon, { getStatusColorClass } from '../../components/StatusIcon';
import { useTranslation } from 'react-i18next';

const SimulationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [workflowFilter, setWorkflowFilter] = useState('all');

  const loadSimulations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (workflowFilter !== 'all') filters.workflow = workflowFilter;

      const data = await simulationService.listSimulations(filters);
      setSimulations(data);
    } catch (err) {
      setError(t('simulations.erreurChargement'));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, workflowFilter, t]);

  useEffect(() => {
    loadSimulations();
  }, [loadSimulations]);

  const handleDelete = async (simulationId) => {
    if (!window.confirm(t('simulations.confirmerSuppression'))) return;

    try {
      await simulationService.deleteSimulation(simulationId);
      setSimulations((prev) => prev.filter((s) => s.simulation_id !== simulationId));
    } catch (err) {
      setError(t('simulations.erreurSuppression'));
    }
  };

  const getDetailUrl = (sim) => {
    return `/app/simulations/${sim.simulation_id}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Simulations</h1>
          <p className="text-dark-400 mt-1">View and manage your simulation history</p>
        </div>
        <Link to="/app/simulations/launch" className="btn-primary">
          + New Simulation
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-dark-400 text-sm">Status:</span>
          <div className="flex gap-1">
            {['all', 'completed', 'running', 'failed'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'text-dark-400 hover:bg-dark-700 hover:text-dark-100'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-dark-400 text-sm">Type:</span>
          <select
            value={workflowFilter}
            onChange={(e) => setWorkflowFilter(e.target.value)}
            className="input-dark"
          >
            <option value="all">{t('simulations.tousTypes')}</option>
            <option value="drone_wing">Drone Wing</option>
            <option value="mach_wing">MACH Wing</option>
          </select>
        </div>

        <button
          onClick={loadSimulations}
          className="ml-auto btn-secondary flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : simulations.length === 0 ? (
        <div className="card-dark p-12 text-center">
          <svg
            className="w-16 h-16 text-dark-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-dark-300 mb-2">{t('simulations.aucuneTrouvee')}</h3>
          <p className="text-dark-500 mb-4">
            {statusFilter !== 'all' || workflowFilter !== 'all'
              ? t('simulations.ajusterFiltres')
              : t('simulations.premiereSimulation')}
          </p>
          <Link to="/app/simulations/launch" className="btn-primary">
            Launch Simulation
          </Link>
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Simulation
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Duration
                </th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {simulations.map((sim) => (
                <tr
                  key={sim.simulation_id}
                  className="hover:bg-dark-700/50 transition-colors cursor-pointer"
                  onClick={() => navigate(getDetailUrl(sim))}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`mr-3 ${getStatusColorClass(sim.status)}`}>
                        <StatusIcon status={sim.status} className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-dark-100 font-mono text-sm">
                          {sim.simulation_id}
                        </p>
                        <p className="text-dark-500 text-xs">
                          {sim.analysis_mode === 'cfd' ? 'CFD Mode' : 'Empirical Mode'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      sim.workflow_type === 'mach_wing'
                        ? 'bg-accent-500/20 text-accent-400'
                        : 'bg-primary-500/20 text-primary-400'
                    }`}>
                      {simulationService.getWorkflowDisplayName(sim.workflow_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: simulationService.getStatusColor(sim.status) + '20',
                        color: simulationService.getStatusColor(sim.status),
                      }}
                    >
                      {sim.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-dark-400 text-sm">{formatDate(sim.created_at)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-dark-400 text-sm">
                      {simulationService.formatDuration(sim.duration_seconds)}
                    </p>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(getDetailUrl(sim))}
                        className="px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 transition-colors"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(sim.simulation_id)}
                        className="px-3 py-1.5 bg-error-500/20 text-error-400 rounded-lg text-sm font-medium hover:bg-error-500/30 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SimulationsPage;
